import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { Switch } from "../../../components/ui/switch";
import { RoleBadge } from "../../../components/common/RoleBadge";

const input = "h-11 min-w-0 rounded-xl border border-slate-200 px-3 text-sm text-ink outline-none focus:border-navy";

const InviteRow = ({ onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ email: "", name: "", role: "ordering" });
  const submit = (e) => {
    e.preventDefault();
    api.post("/team/invite", f).then(() => { toast.success(t("x.team.invited")); setF({ email: "", name: "", role: "ordering" }); onDone(); })
      .catch((err) => toast.error(err.response?.data?.detail === "email_exists" ? t("x.team.exists") : t("common.error")));
  };
  return (
    <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]" data-testid="team-invite-form">
      <input required placeholder={t("users.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="team-invite-name" />
      <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} data-testid="team-invite-email" />
      <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={input} data-testid="team-invite-role">
        <option value="ordering">{t("ops.role.customer_ordering")}</option><option value="admin">{t("ops.role.customer_admin")}</option>
      </select>
      <button type="submit" className="h-11 rounded-full bg-brand px-5 text-sm font-semibold text-navy" data-testid="team-invite-submit">{t("x.team.invite")}</button>
    </form>
  );
};

const Member = ({ m, me, onChange }) => {
  const { t } = useI18n();
  const patch = (body) => api.patch(`/team/${m.id}`, body).then(onChange).catch(() => toast.error(t("common.error")));
  return (
    <li className={`flex flex-col gap-2 py-3 sm:flex-row sm:flex-wrap sm:items-center ${m.active ? "" : "opacity-60"}`} data-testid={`team-member-${m.email}`}>
      <div className="min-w-0 sm:flex-1"><p className="break-words text-sm font-semibold text-navy">{m.name}</p><p className="break-all text-xs text-slate-500">{m.email}{m.status === "invited" && ` · ${t("x.team.pending")}`}</p></div>
      {m.email === me ? <div><RoleBadge role={`customer_${m.role}`} /></div> : (
        <div className="flex flex-wrap items-center gap-2">
          <select value={m.role} onChange={(e) => patch({ role: e.target.value })} className="h-10 rounded-xl border border-slate-200 bg-white px-2 text-xs text-navy" data-testid={`team-role-${m.email}`}>
            <option value="ordering">{t("ops.role.customer_ordering")}</option><option value="admin">{t("ops.role.customer_admin")}</option>
          </select>
          <button type="button" onClick={() => patch({ active: !m.active })} className="h-10 rounded-full bg-canvas px-3 text-xs font-semibold text-navy" data-testid={`team-toggle-${m.email}`}>{m.active ? t("x.team.deactivate") : t("x.team.activate")}</button>
          {m.status === "invited" && m.active && <button type="button" onClick={() => api.post(`/team/${m.id}/resend-invite`).then(() => toast.success(t("x.team.invited")))} className="h-10 rounded-full px-3 text-xs font-semibold text-vblue-ink" data-testid={`team-resend-${m.email}`}>{t("x.team.resend")}</button>}
        </div>
      )}
    </li>
  );
};

export const TeamPanel = ({ me }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/team").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!data) return null;
  const setApproval = (v) => api.put("/team/settings", { require_approval: v }).then(() => { setData({ ...data, require_approval: v }); toast.success(t("x.mail.saved")); });
  return (
    <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm" data-testid="team-panel">
      <h2 className="text-base font-semibold text-navy md:text-lg">{t("x.team.title")}</h2>
      <label className="flex items-center justify-between gap-3 rounded-xl bg-canvas p-3 text-sm text-ink">
        <span>{t("x.team.approval")}</span>
        <Switch checked={data.require_approval} onCheckedChange={setApproval} data-testid="team-require-approval" className="data-[state=checked]:bg-navy" />
      </label>
      <ul className="divide-y divide-slate-100">{data.members.map((m) => <Member key={m.id} m={m} me={me} onChange={load} />)}</ul>
      <InviteRow onDone={load} />
    </section>
  );
};
